import { Button } from "@/components/ui/button";
import Link from "@/components/ui/link";
import { useLng } from "@/hooks/use-lng";
import { useTranslation } from "@/lib/i18n/client";
import { cn } from "@/lib/utils";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

export type NotFoundEntity =
  | "course"
  | "session"
  | "allotment"
  | "product"
  | "articlegroup"
  | "page";

const ENTITY_BY_SEGMENT: Record<string, NotFoundEntity> = {
  courses: "course",
  sessions: "session",
  allotments: "allotment",
  products: "product",
  articlegroups: "articlegroup"
};

function entityFromPathname(pathname: string): NotFoundEntity {
  const segment = pathname.split("/").filter(Boolean)[0];
  return (segment && ENTITY_BY_SEGMENT[segment]) || "page";
}

const RISE = "animate-rise";

const delay = (seconds: string) => ({ animationDelay: seconds });

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function hasCoarsePointer() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(pointer: coarse)").matches;
}

export interface NotFoundPageProps {
  entity?: NotFoundEntity;
}

export function NotFoundPage({ entity }: NotFoundPageProps = {}) {
  const lng = useLng();
  const { t } = useTranslation(lng, "translation");
  const location = useLocation();

  const resolvedEntity = entity ?? entityFromPathname(location.pathname);
  const suffix = resolvedEntity === "page" ? "" : `-${resolvedEntity}`;

  const rootRef = useRef<HTMLDivElement>(null);
  const eyeRef = useRef<HTMLSpanElement>(null);
  const pupilRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (prefersReducedMotion() || hasCoarsePointer()) return;

    const onMove = (event: MouseEvent) => {
      const root = rootRef.current;
      if (root) {
        root.style.setProperty("--mx", `${event.clientX}px`);
        root.style.setProperty("--my", `${event.clientY}px`);
      }

      const eye = eyeRef.current;
      const pupil = pupilRef.current;
      if (!eye || !pupil) return;

      const rect = eye.getBoundingClientRect();
      const dx = event.clientX - (rect.left + rect.width / 2);
      const dy = event.clientY - (rect.top + rect.height / 2);
      const distance = Math.hypot(dx, dy) || 1;
      const offset = Math.min(distance / 6, rect.width * 0.17);

      pupil.style.transform = `translate(${(dx / distance) * offset}px, ${(dy / distance) * offset}px)`;
    };

    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <div
      ref={rootRef}
      className="not-found-page bg-background text-foreground relative flex min-h-[70dvh] w-full flex-1 items-center justify-center overflow-hidden transition-colors"
    >
      <div aria-hidden className="not-found-spotlight pointer-events-none absolute inset-0" />

      <div className="relative max-w-[44rem] px-6 py-8 text-center">
        <div
          aria-hidden
          className="mt-7 flex items-center justify-center text-[clamp(7rem,22vw,13rem)] font-medium tracking-[-0.05em] select-none"
        >
          <span className="animate-rise-slow block leading-[0.74]" style={delay("0.1s")}>
            4
          </span>
          <span className="animate-rise-slow ml-[0.7rem] flex" style={delay("0.18s")}>
            <span
              ref={eyeRef}
              className="animate-eye-bob relative mx-[0.03em] flex h-[0.75em] w-[0.6em] items-center justify-center rounded-full border-[0.104em] border-current"
            >
              <span ref={pupilRef} className="block transition-transform duration-100 ease-out">
                <span className="animate-eye-blink block h-[0.13em] w-[0.13em] origin-center rounded-full bg-current" />
              </span>
            </span>
          </span>
          <span className="animate-rise-slow block leading-[0.74]" style={delay("0.26s")}>
            4
          </span>
        </div>

        <h1
          className={cn(
            "mt-9 text-[clamp(1.35rem,3vw,1.9rem)] font-semibold tracking-[-0.02em]",
            RISE
          )}
          style={delay("0.32s")}
        >
          {t(`not-found-heading${suffix}`)}
        </h1>

        <p
          className={cn(
            "text-muted-foreground mx-auto mt-3 max-w-lg text-[0.95rem] leading-relaxed text-pretty",
            RISE
          )}
          style={delay("0.4s")}
        >
          {t(`not-found-body${suffix}`)}
        </p>

        <div
          className={cn("mt-9 flex flex-wrap justify-center gap-3", RISE)}
          style={delay("0.48s")}
        >
          <Button variant="outline" onClick={() => window.history.back()}>
            {t("not-found-go-back")}
          </Button>
          <Button asChild>
            <Link className="no-underline" to="/">
              {t("not-found-back-home")}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
